/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { Box, Button, ButtonProps, MenuItem, MenuItemProps, styled } from '@mui/material';
import { colors } from '@farmlands/mui-theme';

import Typography from '../Typography';
import { Banner, BannerProps } from '../Banner';
import { shouldForwardProp } from '../../util/helpers';

const mapping = {
  transparent: 'transparent',
  lightest: colors.primary25,
  lighter: colors.primary50,
  light: colors.primary100,
  medium: colors.primary200,
  heavy: colors.primary300,
};

const hover = {
  transparent: 'rgba(255, 255, 255, 0.2)',
  lightest: colors.primary25,
  lighter: colors.primary50,
  light: colors.primary50,
  medium: colors.primary100,
  heavy: colors.primary200,
};

const active = {
  transparent: 'rgba(255, 255, 255, 0.2)',
  lightest: colors.primary25,
  lighter: colors.primary50,
  light: colors.primary200,
  medium: colors.primary300,
  heavy: colors.primary400,
};

interface OrderLocationButtonProps extends Props {
  shade?: PProps['shade'];
}

interface Props {
  buttonTestId: string;
  onClick: () => void;
  icon?: string | React.ReactNode;
  title: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  isEditEnabled?: boolean;
}

type PadActionButtonProps = Props & {
  actionTitle?: string;
  actionIcon?: string;
  customPadding?: string;
  banner?: BannerProps;
  hasError?: boolean;
};

interface PProps extends ButtonProps {
  shade: keyof typeof mapping;
  errorMessage?: string;
  isEditEnabled?: boolean;
}

type PadProps = PProps & {
  customPadding?: string;
  banner?: BannerProps;
};

export const WrapperA = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  overflow: hidden;
`;

export const PadContent = styled('span')`
  ${({ theme }) => theme.typography.body2}
  color: ${({ theme }) => theme.colors.primary500};
  text-align: start;
`;

export const BoldWrapper = styled('div')`
  display: inline-flex;
  align-items: center;
`;
export const PadIcon = styled('span')`
  font-weight: 400;
  font-size: 16px;
  margin-right: 4px;
`;

export const PadHeader = styled('span')`
  font-size: 16px;
  font-weight: 600;
`;

const PadContainer = styled(Box)`
  display: flex;
  flex-direction: column;
`;

export const StyledPadButton = styled(Button, {
  shouldForwardProp: (prop) => shouldForwardProp(['isEditEnabled', 'shade', 'banner'], prop),
})<PadProps>(({ theme, ...props }) => ({
  backgroundColor: mapping[props.shade],
  border: `1px solid ${props['aria-invalid'] ? theme.colors.error500 : 'transparent'}`,
  borderRadius: 8,
  borderBottomLeftRadius: props.banner ? 0 : 8,
  borderBottomRightRadius: props.banner ? 0 : 8,
  padding: props.customPadding ?? '1rem lrem',
  textDecoration: 'none',
  cursor: props.isEditEnabled ? 'pointer' : 'default',
  justifyContent: 'flex-start',

  ':hover': {
    backgroundColor: props.isEditEnabled ? hover[props.shade] : mapping[props.shade],
    cursor: props.isEditEnabled ? 'pointer' : 'default',
  },
  ':active': {
    backgroundColor: props.isEditEnabled ? active[props.shade] : mapping[props.shade],
  },
}));

type PadListItemProps = {
  'data-testid': string;
  option: {
    title: string | React.ReactNode;
    subtitle: string | React.ReactNode;
    sideElement?: React.ReactNode;
  };
} & MenuItemProps;

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.colors.primary50,
  marginBottom: 8,
  borderRadius: 8,

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    minHeight: 'unset',
    gap: '8px',
  },
}));

const AutocompleteTextPreview = styled('div')({
  maxWidth: '100%',
  whitespace: 'nowrap',
  marginLeft: '4px',
});

const BoxLining = styled(Box)`
  display: flex;
  flex: 1 1;
  justify-content: space-between;
  align-items: center;
  border-left: 5px solid ${colors.primary500};
  padding-left: 12px;
  min-height: 96px;
  height: auto;
`;

export const PadLineItem = ({ option, ...props }: PadListItemProps) => {
  return (
    <StyledMenuItem {...props}>
      <AutocompleteTextPreview>
        <Typography
          variant='body1'
          color={'primary700'}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {option.title}
        </Typography>
        <Typography variant='body2' sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {option.subtitle}
        </Typography>
      </AutocompleteTextPreview>
      {option.sideElement}
    </StyledMenuItem>
  );
};

export const PadActionButton: React.FC<PadActionButtonProps> = ({
  buttonTestId,
  onClick,
  title,
  actionIcon,
  actionTitle,
  banner,
  customPadding,
  icon,
  isEditEnabled,
  subtitle,
  hasError,
}) => {
  return (
    <PadButton
      shade='light'
      onClick={onClick}
      disableRipple={!isEditEnabled}
      data-testid={buttonTestId}
      isEditEnabled={isEditEnabled}
      banner={banner}
      customPadding={customPadding}
      aria-invalid={hasError}
    >
      <BoxLining>
        <WrapperA>
          <BoldWrapper>
            {icon && <PadIcon className='material-symbols-outlined'>{icon}</PadIcon>}
            <PadContent sx={{ fontWeight: 700 }}>{title}</PadContent>
          </BoldWrapper>
          {subtitle && <PadContent>{subtitle}</PadContent>}
        </WrapperA>
        {isEditEnabled && (
          <BoldWrapper sx={{ marginLeft: 'auto' }}>
            <PadContent
              sx={{
                textDecoration: 'underline',
                textDecorationThickness: '1px',
                textUnderlineOffset: '2px',
              }}
            >
              {actionTitle ?? 'Edit'}
            </PadContent>
            <PadIcon
              sx={{ marginLeft: '4px', marginRight: '0px' }}
              className='material-symbols-outlined'
            >
              {actionIcon ?? 'edit'}
            </PadIcon>
          </BoldWrapper>
        )}
      </BoxLining>
    </PadButton>
  );
};

export const OrderLocationButton: React.FC<OrderLocationButtonProps> = ({
  shade,
  buttonTestId,
  onClick,
  title,
  icon,
  isEditEnabled,
  subtitle,
}) => {
  return (
    <PadButton
      shade={shade ?? 'medium'}
      onClick={onClick}
      disableRipple={!isEditEnabled}
      data-testid={buttonTestId}
      isEditEnabled={isEditEnabled}
      sx={{ p: 1 }}
    >
      <WrapperA>
        <BoldWrapper>
          <PadIcon className='material-symbols-outlined' sx={{ fontSize: 24, fontWeight: 300 }}>
            {icon}
          </PadIcon>
          <PadContent sx={{ fontWeight: 700 }}>{title}</PadContent>
        </BoldWrapper>
        <BoldWrapper>
          <PadContent>{subtitle}</PadContent>
        </BoldWrapper>
      </WrapperA>
      {isEditEnabled && (
        <BoldWrapper sx={{ marginLeft: 'auto', paddingLeft: '16px' }}>
          <PadContent
            sx={{
              textDecoration: 'underline',
              textDecorationThickness: '1px',
              textUnderlineOffset: '2px',
            }}
          >
            Edit
          </PadContent>
          <PadIcon
            sx={{ marginLeft: '4px', marginRight: '0px' }}
            className='material-symbols-outlined'
          >
            edit
          </PadIcon>
        </BoldWrapper>
      )}
    </PadButton>
  );
};

export const PadButton = ({ errorMessage, ...props }: PadProps) => {
  return (
    <PadContainer>
      <StyledPadButton {...props} />
      {props.banner && (
        <Banner
          sx={{ borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}
          {...props.banner}
        />
      )}
      {props['aria-invalid'] && errorMessage && (
        <Typography variant='body2' color={'error700'} id={props['aria-errormessage']}>
          {errorMessage}
        </Typography>
      )}
    </PadContainer>
  );
};
